import React, { useState, useEffect, useMemo, useRef } from "react";
import DragDropQuestion from "./DragDropQuestion";
import QuestionPaletteModal from "./QuestionPaletteModal";
import CustomConfirmModal from "./CustomConfirmModal";
import QuestionNotesModal from "./QuestionNotesModal";
import EditQuestionModal from "./Admin/EditQuestionModal";
import MobileBottomBar from "./MobileBottomBar";
import { formatExplanation } from "../utils/formatExplanation";
import {
  resolveOriginalSourceImage,
  resolveExplanation,
  remapExplanationToDisplayedOptions,
  getMasterQuestion,
} from "../utils/questionSourceHelper";
import { calculateTotalPoints, evaluateDragDrop } from "../utils/examScoring";
import { formatOptionText } from "../utils/formatOptionText";
import { cleanBankTitle } from "../utils/bankTitle";

function renderFormattedPrompt(rawText) {
  if (!rawText) return null;

  // Convert inline asterisk/bullet points like "following requirements: * It must..." into newlines
  const preformatted = rawText
    .replace(/([^\n])\s+[*•]\s+/g, "$1\n* ")
    .replace(/([^\n])\s+(\(Choose\s+)/gi, "$1\n\n$2");

  const lines = preformatted.split(/\r?\n/);
  const elements = [];
  let currentList = [];

  const flushList = () => {
    if (currentList.length > 0) {
      elements.push(
        <ul key={`list-${elements.length}`} className="prompt-bullet-list">
          {currentList.map((item, idx) => (
            <li key={idx} className="prompt-bullet-item">
              <span className="prompt-bullet-dot">▪</span>
              <span className="prompt-bullet-text">{item}</span>
            </li>
          ))}
        </ul>
      );
      currentList = [];
    }
  };

  lines.forEach((rawLine, idx) => {
    const trimmed = rawLine.trim();
    if (!trimmed) {
      flushList();
      return;
    }

    const bulletMatch = trimmed.match(/^[*•-]\s*(.+)$/);
    if (bulletMatch) {
      currentList.push(bulletMatch[1]);
    } else {
      flushList();
      elements.push(
        <p key={`p-${idx}`} className="prompt-paragraph">
          {trimmed}
        </p>
      );
    }
  });

  flushList();

  return <div className="boson-question-prompt-content">{elements}</div>;
}

function hasEmbeddedCliOptions(question) {
  if (!Array.isArray(question?.options)) return false;

  const optionsText = question.options.join(" ");
  return /gateway of last resort/i.test(optionsText) && /\bO E2\b/.test(optionsText);
}

function AnswerStatus({ variant = "correct", children }) {
  const isCorrect = variant === "correct";
  const isNeutral = variant === "info";
  const label = isCorrect ? "Correct answer" : isNeutral ? "Answer information" : "Incorrect answer";

  return (
    <div className={`answer-status answer-status-${variant}`} role="status">
      <span className="answer-status-icon" aria-hidden="true">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="9" />
          {isCorrect ? (
            <path d="m8 12 2.6 2.6L16.5 9" />
          ) : isNeutral ? (
            <path d="M12 10v5m0-8h.01" />
          ) : (
            <path d="m9 9 6 6m0-6-6 6" />
          )}
        </svg>
      </span>
      <span className="answer-status-label">
        <span className="sr-only">{label}: </span>
        {children}
      </span>
    </div>
  );
}

function QuestionView({
  question,
  seqNumber,
  numQuestions,
  answer,
  answers,
  questions,
  allQuestions = [],
  dispatch,
  examMode,
  settings,
  selectedBankName,
  selectedBankKey,
  flaggedQuestions,
  revealedQuestions = [],
  committedQuestions = [],
  isReviewMode = false,
  onToggleFlag,
  onGoToQuestion,
  onFinishExam,
  onExitReview,
  onExitToDashboard,
  points,
  maxPossiblePoints,
  candidateName,
  currentUser,
  isAdmin,
  secondsRemaining,
  isPaused = false,
  onTogglePause,
  serverConnectionError = null,
  onRetryConnection,
  isRetryingConnection = false,
  isNavSaving = false,
  onRevealAnswer,
}) {
  const isUserAdmin = Boolean(
    isAdmin ||
    (currentUser && (currentUser.role === "admin" || currentUser.email === "candidate@ccna.com"))
  );

  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [toast, setToast] = useState(null); // { type, title, message }
  const [isCheckingAnswer, setIsCheckingAnswer] = useState(false);
  const toastTimeoutRef = useRef(null);

  const showToast = (messageOrObj, type = "success", title = null, duration = 4000) => {
    if (toastTimeoutRef.current) clearTimeout(toastTimeoutRef.current);
    if (!messageOrObj) {
      setToast(null);
      return;
    }

    if (typeof messageOrObj === "object") {
      setToast({
        type: messageOrObj.type || "info",
        title:
          messageOrObj.title ||
          (messageOrObj.type === "error"
            ? "Server Connection Lost"
            : messageOrObj.type === "warning"
            ? "Warning"
            : "Update Applied"),
        message: messageOrObj.message || messageOrObj.desc || "",
      });
      toastTimeoutRef.current = setTimeout(() => setToast(null), messageOrObj.duration || duration);
      return;
    }

    const rawStr = String(messageOrObj);
    const isError = type === "error" || /server\s+connection|offline|failed|error/i.test(rawStr);
    const actualType = isError ? "error" : type;
    const actualTitle =
      title ||
      (actualType === "error"
        ? "Server Connection Lost"
        : actualType === "warning"
        ? "Warning"
        : "Update Applied");

    const cleanMsg = rawStr.replace(/^[⚠️❌✅ℹ️\s]+/, "");
    setToast({
      type: actualType,
      title: actualTitle,
      message: cleanMsg,
    });
    toastTimeoutRef.current = setTimeout(() => setToast(null), duration);
  };

  const setToastMsg = (msg) => {
    if (!msg) {
      setToast(null);
      return;
    }
    showToast(msg);
  };

  // Dynamic font size scaling for Answer and Explanation section
  const [explFontScale, setExplFontScale] = useState(() => {
    try {
      const saved = localStorage.getItem("ccna_expl_font_scale");
      const num = parseFloat(saved);
      if (!isNaN(num) && num >= 0.8 && num <= 1.8) {
        return num;
      }
    } catch {
      // ignore
    }
    return 1.0;
  });

  const handleIncreaseFont = () => {
    setExplFontScale((prev) => {
      const next = Math.min(1.8, Math.round((prev + 0.1) * 10) / 10);
      try {
        localStorage.setItem("ccna_expl_font_scale", String(next));
      } catch {}
      return next;
    });
  };

  const handleDecreaseFont = () => {
    setExplFontScale((prev) => {
      const next = Math.max(0.8, Math.round((prev - 0.1) * 10) / 10);
      try {
        localStorage.setItem("ccna_expl_font_scale", String(next));
      } catch {}
      return next;
    });
  };

  const handleResetFont = () => {
    setExplFontScale(1.0);
    try {
      localStorage.setItem("ccna_expl_font_scale", "1.0");
    } catch {}
  };

  useEffect(() => {
    return () => {
      if (toastTimeoutRef.current) clearTimeout(toastTimeoutRef.current);
    };
  }, []);

  const activeBankTitle = cleanBankTitle(selectedBankName || question?.bankName || "CCNA Exam");
  const hasExhibitImage = Boolean(question?.exhibitImage);

  const [exhibitZoom, setExhibitZoom] = useState(1);
  const [exhibitPan, setExhibitPan] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const panStartRef = useRef({ x: 0, y: 0, panX: 0, panY: 0 });
  const pinchStartRef = useRef(null);

  const [showOriginalSource, setShowOriginalSource] = useState(false);

  const handleToggleOriginalSource = () => {
    setShowOriginalSource((prev) => {
      const shouldShow = !prev;
      if (shouldShow) {
        setSourceImgError(false);
        setIsSourceImageLoading(true);
      }
      return shouldShow;
    });
  };

  const handleCloseOriginalSource = () => {
    setShowOriginalSource(false);
    setIsSourceImageLoading(false);
  };

  const [sourceZoom, setSourceZoom] = useState(1);
  const [sourcePan, setSourcePan] = useState({ x: 0, y: 0 });
  const [isPanningSource, setIsPanningSource] = useState(false);
  const panSourceStartRef = useRef({ x: 0, y: 0, panX: 0, panY: 0 });
  const pinchSourceStartRef = useRef(null);
  const [sourceImgError, setSourceImgError] = useState(false);
  const [isSourceImageLoading, setIsSourceImageLoading] = useState(false);

  const [imgError, setImgError] = useState(false);
  const [isExhibitImageLoading, setIsExhibitImageLoading] = useState(hasExhibitImage);
  const [isNavTransitioning, setIsNavTransitioning] = useState(false);

  useEffect(() => {
    setIsNavTransitioning(true);

    // 1. Hide original source dump when navigating to any question
    setShowOriginalSource(false);
    setSourceZoom(1);
    setSourcePan({ x: 0, y: 0 });
    setSourceImgError(false);
    setIsSourceImageLoading(false);

    // 2. Reset exhibit zoom/pan and image errors
    setExhibitZoom(1);
    setExhibitPan({ x: 0, y: 0 });
    setImgError(false);
    setIsExhibitImageLoading(hasExhibitImage);

    // 3. Close inline note box
    setIsNoteBoxOpen(false);

    // 4. Fall strictly on top of the screen so candidate sees entire statement, header, and items
    const scrollToScreenTop = () => {
      window.scrollTo({ top: 0, left: 0, behavior: "instant" });
      if (document.documentElement) document.documentElement.scrollTop = 0;
      if (document.body) document.body.scrollTop = 0;

      const mainContainer =
        document.querySelector(".cisco-simulator-root") ||
        document.querySelector(".simulator-app-container") ||
        document.querySelector(".boson-exsim-view") ||
        document.getElementById("root");
      if (mainContainer) mainContainer.scrollTop = 0;
    };

    scrollToScreenTop();
    requestAnimationFrame(scrollToScreenTop);
    const scrollTimer1 = setTimeout(scrollToScreenTop, 50);
    const scrollTimer2 = setTimeout(scrollToScreenTop, 180);

    const timer = setTimeout(() => {
      setIsNavTransitioning(false);
    }, 360);
    return () => {
      clearTimeout(scrollTimer1);
      clearTimeout(scrollTimer2);
      clearTimeout(timer);
    };
  }, [seqNumber, hasExhibitImage]);

  const isBusyNavigating = Boolean(isNavTransitioning || isNavSaving);
  const [showPaletteModal, setShowPaletteModal] = useState(false);
  const [isNoteBoxOpen, setIsNoteBoxOpen] = useState(false);
  const [isAllNotesModalOpen, setIsAllNotesModalOpen] = useState(false);
  const [currentNoteText, setCurrentNoteText] = useState("");

  const [questionComments, setQuestionComments] = useState({});

  // Fetch live candidate notes from MySQL database (strict per-user privacy isolation)
  useEffect(() => {
    const fetchLiveNotes = async () => {
      try {
        const storedUser = (() => {
          try { return JSON.parse(localStorage.getItem("ccna_auth_user") || "{}"); } catch { return {}; }
        })();
        const uid = currentUser?.id || storedUser?.id;
        const uemail = currentUser?.email || storedUser?.email;
        const token = localStorage.getItem("ccna_auth_token") || "";

        // If no user is authenticated, clear notes so guests/anonymous users never see private user notes
        if (!uid && !uemail && !token) {
          setQuestionComments({});
          return;
        }

        const query = uid
          ? `?userId=${encodeURIComponent(uid)}&userEmail=${encodeURIComponent(uemail || "")}&_t=${Date.now()}`
          : uemail
          ? `?userEmail=${encodeURIComponent(uemail)}&_t=${Date.now()}`
          : `?_t=${Date.now()}`;

        const headers = { Accept: "application/json", "Cache-Control": "no-cache" };
        if (token) {
          headers["Authorization"] = `Bearer ${token}`;
        }

        const res = await fetch(`/api/notes${query}`, { headers });
        const data = await res.json();
        if (data && data.notes) {
          setQuestionComments(data.notes);
        } else {
          setQuestionComments({});
        }
      } catch (err) {
        setQuestionComments({});
      }
    };
    fetchLiveNotes();
  }, [currentUser?.id, currentUser?.email]);

  // Filter notes strictly to questions belonging to the currently active exam bank (no duplicates)
  const currentBankNotes = useMemo(() => {
    if (!Array.isArray(questions) || questions.length === 0) return {};
    const bankNotes = {};

    questions.forEach((q) => {
      if (!q) return;
      const qId = q?.id !== undefined && q?.id !== null ? String(q.id) : null;
      const qNo = q?.questionNo ? String(q.questionNo) : null;

      const noteText =
        (qId && questionComments[qId]) ||
        (qNo && questionComments[qNo]) ||
        "";

      if (noteText && noteText.trim().length > 0) {
        const canonicalKey = qId || qNo;
        bankNotes[canonicalKey] = noteText.trim();
      }
    });

    return bankNotes;
  }, [questions, questionComments]);

  const currentBankNotesCount = Object.keys(currentBankNotes).length;

  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    title: "",
    message: "",
    confirmText: "Confirm",
    cancelText: "Cancel",
    type: "warning",
    onConfirm: () => {},
  });

  const questionKey = question?.id || question?.questionNo || seqNumber;
  const existingComment = questionComments[questionKey] || questionComments[String(question?.id)] || "";

  useEffect(() => {
    setImgError(false);
    setSourceImgError(false);
    setSourceZoom(1);
    setSourcePan({ x: 0, y: 0 });
    setIsPanningSource(false);
    setCurrentNoteText(existingComment);
    setIsNoteBoxOpen(false);
    setExhibitZoom(1);
    setExhibitPan({ x: 0, y: 0 });
    setIsPanning(false);
  }, [question?.id, seqNumber, existingComment]);

  useEffect(() => {
    if (isPaused) {
      setIsNoteBoxOpen(false);
      setShowPaletteModal(false);
      setIsAllNotesModalOpen(false);
      setIsPanning(false);
      setIsPanningSource(false);
    }
  }, [isPaused]);

  // Inline Zoom & Pan handlers for Exhibit
  const handleZoomIn = (e) => {
    e?.stopPropagation();
    setExhibitZoom((prev) => Math.min(3.5, +(prev + 0.3).toFixed(1)));
  };

  const handleZoomOut = (e) => {
    e?.stopPropagation();
    setExhibitZoom((prev) => {
      const next = Math.max(1, +(prev - 0.3).toFixed(1));
      if (next === 1) setExhibitPan({ x: 0, y: 0 });
      return next;
    });
  };

  const handleResetZoom = (e) => {
    e?.stopPropagation();
    setExhibitZoom(1);
    setExhibitPan({ x: 0, y: 0 });
  };

  const handleMouseDown = (e) => {
    if (exhibitZoom <= 1) return;
    setIsPanning(true);
    panStartRef.current = {
      x: e.clientX,
      y: e.clientY,
      panX: exhibitPan.x,
      panY: exhibitPan.y,
    };
  };

  const handleMouseMove = (e) => {
    if (!isPanning || exhibitZoom <= 1) return;
    const dx = e.clientX - panStartRef.current.x;
    const dy = e.clientY - panStartRef.current.y;
    setExhibitPan({
      x: panStartRef.current.panX + dx,
      y: panStartRef.current.panY + dy,
    });
  };

  const handleMouseUpOrLeave = () => {
    if (isPanning) {
      setIsPanning(false);
    }
  };

  const handleTouchStart = (e) => {
    if (e.touches.length === 2) {
      e.preventDefault();
      const dx = e.touches[0].clientX - e.touches[1].clientX;
      const dy = e.touches[0].clientY - e.touches[1].clientY;
      pinchStartRef.current = {
        distance: Math.hypot(dx, dy),
        zoom: exhibitZoom,
      };
      setIsPanning(false);
      return;
    }
    if (exhibitZoom <= 1 || e.touches.length !== 1) return;
    setIsPanning(true);
    panStartRef.current = {
      x: e.touches[0].clientX,
      y: e.touches[0].clientY,
      panX: exhibitPan.x,
      panY: exhibitPan.y,
    };
  };

  const handleTouchMove = (e) => {
    if (e.touches.length === 2 && pinchStartRef.current) {
      e.preventDefault();
      const dx = e.touches[0].clientX - e.touches[1].clientX;
      const dy = e.touches[0].clientY - e.touches[1].clientY;
      const distance = Math.hypot(dx, dy);
      const nextZoom = Math.min(3.5, Math.max(1, +(pinchStartRef.current.zoom * (distance / pinchStartRef.current.distance)).toFixed(2)));
      setExhibitZoom(nextZoom);
      if (nextZoom === 1) setExhibitPan({ x: 0, y: 0 });
      return;
    }
    if (!isPanning || exhibitZoom <= 1 || e.touches.length !== 1) return;
    e.preventDefault();
    const dx = e.touches[0].clientX - panStartRef.current.x;
    const dy = e.touches[0].clientY - panStartRef.current.y;
    setExhibitPan({
      x: panStartRef.current.panX + dx,
      y: panStartRef.current.panY + dy,
    });
  };

  const handleTouchEnd = (e) => {
    if (!e.touches || e.touches.length < 2) pinchStartRef.current = null;
    setIsPanning(false);
  };

  // Inline Zoom & Pan handlers for Original Source Dump
  const handleSourceZoomIn = (e) => {
    e?.stopPropagation();
    setSourceZoom((prev) => Math.min(3.5, +(prev + 0.3).toFixed(1)));
  };

  const handleSourceZoomOut = (e) => {
    e?.stopPropagation();
    setSourceZoom((prev) => {
      const next = Math.max(1, +(prev - 0.3).toFixed(1));
      if (next === 1) setSourcePan({ x: 0, y: 0 });
      return next;
    });
  };

  const handleSourceResetZoom = (e) => {
    e?.stopPropagation();
    setSourceZoom(1);
    setSourcePan({ x: 0, y: 0 });
  };

  const handleSourceMouseDown = (e) => {
    if (sourceZoom <= 1) return;
    setIsPanningSource(true);
    panSourceStartRef.current = {
      x: e.clientX,
      y: e.clientY,
      panX: sourcePan.x,
      panY: sourcePan.y,
    };
  };

  const handleSourceMouseMove = (e) => {
    if (!isPanningSource || sourceZoom <= 1) return;
    const dx = e.clientX - panSourceStartRef.current.x;
    const dy = e.clientY - panSourceStartRef.current.y;
    setSourcePan({
      x: panSourceStartRef.current.panX + dx,
      y: panSourceStartRef.current.panY + dy,
    });
  };

  const handleSourceMouseUpOrLeave = () => {
    if (isPanningSource) setIsPanningSource(false);
  };

  const handleSourceTouchStart = (e) => {
    if (!e.touches || e.touches.length === 0) return;

    if (e.touches.length === 2) {
      e.preventDefault();
      const dx = e.touches[0].clientX - e.touches[1].clientX;
      const dy = e.touches[0].clientY - e.touches[1].clientY;
      pinchSourceStartRef.current = {
        distance: Math.hypot(dx, dy),
        zoom: sourceZoom,
      };
      setIsPanningSource(false);
      return;
    }

    if (sourceZoom <= 1) return;
    const touch = e.touches[0];
    setIsPanningSource(true);
    panSourceStartRef.current = {
      x: touch.clientX,
      y: touch.clientY,
      panX: sourcePan.x,
      panY: sourcePan.y,
    };
  };

  const handleSourceTouchMove = (e) => {
    if (!e.touches || e.touches.length === 0) return;

    if (e.touches.length === 2 && pinchSourceStartRef.current) {
      e.preventDefault();
      const dx = e.touches[0].clientX - e.touches[1].clientX;
      const dy = e.touches[0].clientY - e.touches[1].clientY;
      const distance = Math.hypot(dx, dy);
      const nextZoom = Math.min(
        3.5,
        Math.max(1, +(pinchSourceStartRef.current.zoom * (distance / pinchSourceStartRef.current.distance)).toFixed(2))
      );
      setSourceZoom(nextZoom);
      if (nextZoom === 1) setSourcePan({ x: 0, y: 0 });
      return;
    }

    if (!isPanningSource || sourceZoom <= 1 || e.touches.length !== 1) return;
    e.preventDefault();
    const touch = e.touches[0];
    const dx = touch.clientX - panSourceStartRef.current.x;
    const dy = touch.clientY - panSourceStartRef.current.y;
    setSourcePan({
      x: panSourceStartRef.current.panX + dx,
      y: panSourceStartRef.current.panY + dy,
    });
  };

  const handleSourceTouchEnd = (e) => {
    if (!e.touches || e.touches.length < 2) pinchSourceStartRef.current = null;
    if (isPanningSource) setIsPanningSource(false);
  };

  const handleSaveComment = (textToSave) => {
    const trimmed = textToSave ? textToSave.trim() : "";
    const updated = { ...questionComments };
    if (trimmed) {
      updated[questionKey] = trimmed;
    } else {
      delete updated[questionKey];
    }
    setQuestionComments(updated);

    // MySQL sync with userId / userEmail
    if (question?.id) {
      const storedUser = (() => {
        try { return JSON.parse(localStorage.getItem("ccna_auth_user") || "{}"); } catch { return {}; }
      })();
      const token = localStorage.getItem("ccna_auth_token") || "";
      const headers = { "Content-Type": "application/json" };
      if (token) headers["Authorization"] = `Bearer ${token}`;

      fetch("/api/notes", {
        method: "POST",
        headers,
        body: JSON.stringify({
          userId: currentUser?.id || storedUser?.id || null,
          userEmail: currentUser?.email || storedUser?.email || null,
          candidateName: currentUser?.name || storedUser?.name || candidateName || "Candidate",
          questionId: question.id,
          questionNo: question.questionNo || `Question #${question.id}`,
          noteText: trimmed,
        }),
      }).catch(() => {});
    }

    setIsNoteBoxOpen(false);
  };

  const handleDeleteComment = (targetIdentifier, targetQNo, rawKey) => {
    let targetQ = null;
    let targetId = null;
    let targetNo = null;

    if (targetIdentifier && typeof targetIdentifier === "object") {
      targetQ = targetIdentifier;
      targetId = targetQ.id;
      targetNo = targetQ.questionNo;
    } else if (targetIdentifier != null || targetQNo != null || rawKey != null) {
      const idStr = targetIdentifier != null ? String(targetIdentifier).trim() : "";
      const qNoStr = targetQNo != null ? String(targetQNo).trim() : "";
      const rawStr = rawKey != null ? String(rawKey).trim() : "";
      const numVal = Number(idStr);

      if (Array.isArray(questions)) {
        targetQ = questions.find(
          (q) =>
            q &&
            (q.id === targetIdentifier ||
              String(q.id) === idStr ||
              (qNoStr && q.questionNo === qNoStr) ||
              (idStr && q.questionNo === idStr) ||
              (rawStr && (String(q.id) === rawStr || q.questionNo === rawStr)))
        );
      }

      if (targetQ) {
        targetId = targetQ.id;
        targetNo = targetQ.questionNo;
      } else {
        targetId = !isNaN(numVal) && numVal > 0 ? numVal : null;
        targetNo = qNoStr || (idStr.startsWith("Question") ? idStr : null);
      }
    } else {
      targetQ = question;
      targetId = question?.id;
      targetNo = question?.questionNo;
    }

    // Comprehensive cleanup of questionComments state across all key representations
    const updated = { ...questionComments };
    const keysToDelete = new Set();

    if (targetId != null) {
      keysToDelete.add(targetId);
      keysToDelete.add(String(targetId));
    }
    if (targetNo) {
      keysToDelete.add(targetNo);
      const digits = targetNo.replace(/\D/g, "");
      if (digits) {
        keysToDelete.add(digits);
        keysToDelete.add(Number(digits));
        keysToDelete.add(`Question #${digits}`);
        keysToDelete.add(`Question ${digits}`);
      }
    }
    if (targetIdentifier && typeof targetIdentifier !== "object") {
      keysToDelete.add(targetIdentifier);
      keysToDelete.add(String(targetIdentifier));
    }
    if (rawKey) {
      keysToDelete.add(rawKey);
      keysToDelete.add(String(rawKey));
    }

    keysToDelete.forEach((k) => {
      delete updated[k];
    });

    setQuestionComments(updated);

    // If the active question on screen is the one being deleted, reset composer
    const isCurrentQuestion =
      (targetId != null && question?.id === targetId) ||
      (targetNo && question?.questionNo === targetNo) ||
      targetIdentifier === questionKey ||
      rawKey === questionKey;

    if (isCurrentQuestion) {
      setCurrentNoteText("");
      setIsNoteBoxOpen(false);
    }

    // MySQL sync with userId / userEmail
    const storedUser = (() => {
      try { return JSON.parse(localStorage.getItem("ccna_auth_user") || "{}"); } catch { return {}; }
    })();
    const token = localStorage.getItem("ccna_auth_token") || "";
    const headers = { "Content-Type": "application/json", Accept: "application/json" };
    if (token) headers["Authorization"] = `Bearer ${token}`;

    const effectiveUserId = currentUser?.id || storedUser?.id || null;
    const effectiveEmail = currentUser?.email || storedUser?.email || null;
    const effectiveName = currentUser?.name || storedUser?.name || candidateName || "Candidate";

    const resolvedQId = targetId || (targetNo ? parseInt(targetNo.replace(/\D/g, ""), 10) : 0);
    const resolvedQNo = targetNo || (targetId ? `Question #${targetId}` : "");

    fetch("/api/notes", {
      method: "POST",
      headers,
      body: JSON.stringify({
        userId: effectiveUserId,
        userEmail: effectiveEmail,
        candidateName: effectiveName,
        questionId: resolvedQId,
        questionNo: resolvedQNo,
        noteText: "",
        action: "delete",
      }),
    }).catch(() => {});
  };

  const isDragDrop = question.type === "drag_drop" || Boolean(question.dragDropData);

  const correctOptions = useMemo(() => {
    const raw = question.correctOptions !== undefined && question.correctOptions !== null
      ? question.correctOptions
      : question.correctOption;
    return (Array.isArray(raw) ? raw : raw !== null && raw !== undefined ? [raw] : [])
      .map(Number)
      .filter((n) => !isNaN(n));
  }, [question.correctOption, question.correctOptions]);

  const isMulti = correctOptions.length > 1;

  const isFlagged = flaggedQuestions?.includes(seqNumber - 1);

  const selectedIndices = useMemo(() => {
    const raw = isMulti
      ? Array.isArray(answer)
        ? answer
        : answer?.selections || []
      : typeof answer === "number"
      ? [answer]
      : Array.isArray(answer)
      ? answer
      : answer?.selections || [];
    return raw.map(Number).filter((n) => !isNaN(n));
  }, [answer, isMulti]);

  const userIsFullyCorrect = useMemo(() => {
    if (correctOptions.length === 0 || selectedIndices.length === 0) return false;
    if (correctOptions.length !== selectedIndices.length) return false;
    const sortedCorr = [...correctOptions].sort((a, b) => a - b).join(",");
    const sortedSel = [...selectedIndices].sort((a, b) => a - b).join(",");
    return sortedCorr === sortedSel;
  }, [correctOptions, selectedIndices]);

  // Exhibit image source resolver
  const getExhibitUrl = (imgPath) => {
    if (!imgPath) return "";
    if (imgPath.startsWith("http://") || imgPath.startsWith("https://")) {
      return imgPath;
    }
    const clean = imgPath.replace(/^\/+/, "");
    const publicUrl = (process.env.PUBLIC_URL || "").replace(/\/+$/, "");
    return publicUrl ? `${publicUrl}/${clean}` : `/${clean}`;
  };

  const exhibitSrc = getExhibitUrl(question.exhibitImage);
  const resolvedOriginalSource =
    question.originalSourceImage || resolveOriginalSourceImage(question);
  const originalSourceSrc = getExhibitUrl(resolvedOriginalSource);

  const isRevealed = Boolean(revealedQuestions?.includes(seqNumber - 1));
  const isCommitted = Boolean(committedQuestions?.includes(seqNumber - 1));
  const maxAllowed = isMulti ? correctOptions.length : 1;
  const isTimeOver = secondsRemaining !== null && secondsRemaining <= 0;
  const isLocked = Boolean(isRevealed || isReviewMode || isTimeOver || isCommitted);

  const handleOptionClick = (index) => {
    // Once answer is revealed, committed, in review mode, or time has expired, user cannot modify their selection
    if (isLocked) return;
    if (serverConnectionError) {
      showToast({
        type: "error",
        title: "Server Connection Lost",
        message: "You cannot submit or change answers while offline.",
        duration: 4500,
      });
      return;
    }

    if (isMulti) {
      const current = selectedIndices;
      if (current.includes(index)) {
        // Deselect clicked item
        const newSelections = current.filter((i) => i !== index);
        dispatch({ type: "multiSelect", payload: newSelections });
      } else {
        if (current.length < maxAllowed) {
          const newSelections = [...current, index];
          dispatch({ type: "multiSelect", payload: newSelections });
        } else if (maxAllowed > 0) {
          // Replace oldest selection if already at capacity
          const newSelections = [...current.slice(1), index];
          dispatch({ type: "multiSelect", payload: newSelections });
        }
      }
    } else {
      // Single choice: select or change to chosen option freely
      dispatch({ type: "newAnswer", payload: index });
    }
  };

  // Evaluated questions = any question committed (answered & advanced) or revealed (Show Answer clicked).
  // Strictly excludes the current active uncommitted question to prevent score leak on mere option click!
  const evaluatedIndices = useMemo(() => {
    if (isReviewMode) {
      return (questions || []).map((_, i) => i);
    }
    const set = new Set();
    (committedQuestions || []).forEach((idx) => set.add(idx));
    (revealedQuestions || []).forEach((idx) => set.add(idx));
    if (Array.isArray(answers)) {
      answers.forEach((ans, idx) => {
        if (
          ans !== null &&
          ans !== undefined &&
          (typeof ans === "number" ||
            typeof ans === "string" ||
            (Array.isArray(ans) && ans.length > 0) ||
            (Array.isArray(ans?.selections) && ans.selections.length > 0) ||
            (ans?.matches && Object.keys(ans.matches).length > 0))
        ) {
          // Prevent answer leak: current active question is excluded until committed or revealed
          if (idx === seqNumber - 1 && !isRevealed && !isCommitted) {
            return;
          }
          set.add(idx);
        }
      });
    }
    return Array.from(set);
  }, [committedQuestions, revealedQuestions, isReviewMode, questions, answers, seqNumber, isRevealed, isCommitted]);

  // Points strictly earned on evaluated questions
  const evaluatedPoints = useMemo(() => {
    if (!questions || evaluatedIndices.length === 0) return 0;
    const filteredAnswers = (questions || []).map((_, i) =>
      evaluatedIndices.includes(i) ? answers[i] : null
    );
    return calculateTotalPoints(questions, filteredAnswers);
  }, [questions, answers, evaluatedIndices]);


  // Points earned: strictly reflects evaluated questions (committed via Next or revealed via Show Answer)
  // Current active uncommitted question is strictly excluded so score NEVER updates on mere option selection
  const effectiveScore = isReviewMode
    ? (typeof points === "number" && points >= 0 ? points : evaluatedPoints)
    : evaluatedPoints;

  // Live score percentage based on total exam maxPossiblePoints (e.g. 10 pts / 500 = 2.0%, 250 pts / 500 = 50.0%)
  const livePercentage =
    maxPossiblePoints > 0
      ? (((effectiveScore || 0) / maxPossiblePoints) * 100).toFixed(1)
      : "0.0";

  const canGoPrev = seqNumber > 1;
  const canGoNext = seqNumber < numQuestions;

  const handleNextClick = async () => {
    if (!canGoNext || isBusyNavigating) return;
    if (serverConnectionError) {
      if (onRetryConnection) onRetryConnection();
      return;
    }

    setShowOriginalSource(false);
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
    if (document.documentElement) document.documentElement.scrollTop = 0;
    if (document.body) document.body.scrollTop = 0;
    const mainContainer =
      document.querySelector(".cisco-simulator-root") ||
      document.querySelector(".simulator-app-container") ||
      document.querySelector(".boson-exsim-view") ||
      document.getElementById("root");
    if (mainContainer) mainContainer.scrollTop = 0;

    try {
      await onGoToQuestion(seqNumber);
    } catch (err) {
      console.error("Next question navigation blocked due to server connection error:", err);
    }
  };

  const handlePrevClick = async () => {
    if (!canGoPrev || isBusyNavigating) return;

    setShowOriginalSource(false);
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
    if (document.documentElement) document.documentElement.scrollTop = 0;
    if (document.body) document.body.scrollTop = 0;
    const mainContainer =
      document.querySelector(".cisco-simulator-root") ||
      document.querySelector(".simulator-app-container") ||
      document.querySelector(".boson-exsim-view") ||
      document.getElementById("root");
    if (mainContainer) mainContainer.scrollTop = 0;

    try {
      await onGoToQuestion(seqNumber - 2);
    } catch (err) {
      console.error("Previous question navigation blocked:", err);
    }
  };

  const handleShowAnswerClick = async () => {
    if (isRevealed || (isCommitted && settings?.showAnswersInline !== false)) return;
    if (serverConnectionError) {
      if (onRetryConnection) onRetryConnection();
      return;
    }
    if (isCheckingAnswer) return;

    const mainContainer =
      document.querySelector(".cisco-simulator-root") ||
      document.querySelector(".simulator-app-container") ||
      document.querySelector(".boson-exsim-view") ||
      document.getElementById("root");
    const viewportPosition = {
      windowTop: window.pageYOffset || document.documentElement.scrollTop || 0,
      containerTop: mainContainer?.scrollTop || 0,
    };

    if (onRevealAnswer) {
      setIsCheckingAnswer(true);
      try {
        await onRevealAnswer(seqNumber - 1);
      } catch (err) {
        console.error("Reveal answer failed due to server connection error:", err);
      } finally {
        setIsCheckingAnswer(false);
      }
    } else {
      dispatch({ type: "revealAnswer", payload: seqNumber - 1 });
    }

    // Keep the chosen option(s) in view after the answer panel mounts.
    const restoreViewport = () => {
      window.scrollTo({ top: viewportPosition.windowTop, left: 0, behavior: "instant" });
      if (mainContainer) mainContainer.scrollTop = viewportPosition.containerTop;
    };
    requestAnimationFrame(() => requestAnimationFrame(restoreViewport));
    setTimeout(restoreViewport, 60);
    setTimeout(restoreViewport, 180);
  };

  const handleExitClick = () => {
    if (isReviewMode) {
      onExitReview ? onExitReview() : onExitToDashboard();
      return;
    }

    setConfirmDialog({
      isOpen: true,
      title: "Save & Return to Dashboard",
      message:
        "Your current question, answers, and remaining time will be saved in real-time. You can resume right where you left off from the Dashboard.",
      confirmText: "Save & Exit",
      cancelText: "Continue Practicing",
      type: "info",
      onConfirm: () => {
        setConfirmDialog((prev) => ({ ...prev, isOpen: false }));
        onExitToDashboard();
      },
    });
  };

  const handleGradeClick = () => {
    const answeredCount = answers.filter(
      (a) =>
        a !== null &&
        a !== undefined &&
        (typeof a === "number" || a?.confirmed || a?.selections?.length > 0)
    ).length;

    setConfirmDialog({
      isOpen: true,
      title: "Submit & Grade Exam",
      message: `You have answered ${answeredCount} of ${numQuestions} questions. Are you ready to submit and calculate your final score?`,
      confirmText: "Grade Exam Now",
      cancelText: "Return to Exam",
      type: "info",
      onConfirm: () => {
        setConfirmDialog((prev) => ({ ...prev, isOpen: false }));
        onFinishExam();
      },
    });
  };

  const getInitials = (name) => {
    if (!name || !name.trim()) return "CC";
    const parts = name.trim().split(/\s+/);
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  };

  const candidateInitials = getInitials(candidateName);

  const formatTime = (secs) => {
    if (secs === null || secs === undefined) return null;
    const m = Math.floor(Math.abs(secs) / 60);
    const s = Math.abs(secs) % 60;
    return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  };

  const isUntimed =
    settings?.timerMode === "not_timed" ||
    settings?.isTimed === false ||
    settings?.timerMode === "none" ||
    secondsRemaining === null;

  const timerDisplay = isUntimed ? null : formatTime(secondsRemaining);
  const timerIsLow = !isUntimed && secondsRemaining !== null && secondsRemaining < 300;

  return (
    <div
      className={`boson-exsim-view ${isPaused ? "is-paused" : ""}`}
      style={{ "--expl-font-scale": explFontScale }}
    >
      {/* SLIM THEME TRANSITION ANIMATED LOADER */}
      <div
        className={`boson-slim-transition-track ${isNavTransitioning ? "is-active" : ""}`}
        aria-hidden="true"
      >
        <div className="boson-slim-transition-bar"></div>
        <div className="boson-slim-transition-glow"></div>
      </div>

      {/* PRIMARY NAVIGATION: keep this row intentionally minimal on every screen. */}
      <div className="boson-top-bar">
        <div className="top-bar-left-actions">
          <button
            type="button"
            className="btn-boson-back"
            onClick={handleExitClick}
            title="Return to Exam Bank"
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="15 18 9 12 15 6" />
            </svg>
            <span>Exam</span>
          </button>
        </div>

        <div className="top-bar-right-group">
          <div
            className="boson-user-avatar"
            title={candidateName ? `Candidate: ${candidateName}` : "CCNA Candidate"}
          >
            <span>{candidateInitials}</span>
          </div>
        </div>
      </div>

      {isBusyNavigating && (
        <div className="question-nav-loading-overlay" role="status" aria-live="polite">
          <span className="question-nav-spinner" aria-hidden="true"></span>
          <span className="sr-only">{isNavSaving ? "Saving your answer and loading the next question" : "Loading question"}</span>
        </div>
      )}

      <div className="boson-exam-info-card">
        {/* SECONDARY EXAM CONTROLS: bank context, timer, pause, and notes. */}
        <div className="boson-exam-context-row">
        {activeBankTitle && (
          <div className="top-bar-bank-tag context-bank-tag" title={`Currently attempting: ${activeBankTitle}`}>
            <span className="bank-tag-name">{activeBankTitle}</span>
          </div>
        )}

        <div className="context-actions">
          {timerDisplay && (
            <div
              className={`exam-timer-pill ${timerIsLow ? "timer-low" : ""} ${isPaused || Boolean(serverConnectionError) ? "timer-paused" : ""}`}
              onClick={!isReviewMode && !serverConnectionError && onTogglePause ? onTogglePause : undefined}
              title={
                serverConnectionError
                  ? "Timer paused due to network disconnection"
                  : !isReviewMode && onTogglePause
                  ? isPaused
                    ? "Exam Paused (Click to resume)"
                    : "Exam Running (Click to pause)"
                  : undefined
              }
              style={{ cursor: !isReviewMode && !serverConnectionError && onTogglePause ? "pointer" : "default" }}
              role={!isReviewMode && !serverConnectionError && onTogglePause ? "button" : undefined}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
              </svg>
              <span>{serverConnectionError ? "⏸️ PAUSED (OFFLINE)" : isPaused ? "⏸️ PAUSED" : timerDisplay}</span>
            </div>
          )}

          {!isReviewMode && onTogglePause && (
            <button
              type="button"
              className={`btn-boson-pause ${isPaused ? "is-paused" : ""}`}
              onClick={onTogglePause}
              title={isPaused ? "Resume Exam" : "Pause Exam (Freeze timer & blur question)"}
            >
              {isPaused ? (
                <>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor">
                    <polygon points="5 3 19 12 5 21 5 3" />
                  </svg>
                  <span>Resume</span>
                </>
              ) : (
                <>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor">
                    <rect x="6" y="4" width="4" height="16" rx="1" />
                    <rect x="14" y="4" width="4" height="16" rx="1" />
                  </svg>
                  <span>Pause</span>
                </>
              )}
            </button>
          )}

          <button
            type="button"
            className={`btn-top-notes-summary ${currentBankNotesCount > 0 ? "has-notes" : ""}`}
            onClick={() => setIsAllNotesModalOpen(true)}
            title="View question notes in this exam bank"
          >
            <span>💬 Notes ({currentBankNotesCount})</span>
          </button>
        </div>
        </div>

        {/* MAIN TITLE HEADER */}
        <div className="boson-main-header">
        <div className="boson-title-bank-row">
          <h1 className="boson-exam-title">
            Cisco 200-301 CCNA Exam Simulator
          </h1>
        </div>

        <div className="boson-sub-header">
          <div className="boson-sub-left">
            <div className="boson-mobile-summary-row">
              <span className="boson-q-count">
                Question {seqNumber} of {numQuestions}
              </span>
              {settings?.showScoreLive !== false && (
                <>
                  <span className="boson-dot-sep">•</span>
                  <span
                    className="boson-live-score"
                    title={`${effectiveScore || 0} / ${maxPossiblePoints} pts`}
                  >
                    {livePercentage}% correct
                  </span>
                </>
              )}
            </div>
            <div className="boson-mobile-detail-row">
              {question.questionNo && (
                <>
                  <span className="boson-dot-sep">•</span>
                  <span className="boson-qno-pill">{question.questionNo}</span>
                </>
              )}
              {isReviewMode && (
                <>
                  <span className="boson-dot-sep">•</span>
                  <span className="boson-review-badge">🔍 Review Mode (Read-Only)</span>
                </>
              )}
              {!isReviewMode && isLocked && (
                <>
                  <span className="boson-dot-sep">•</span>
                  <span className="boson-review-badge" style={{ background: "rgba(100, 116, 139, 0.2)", color: "#94a3b8", border: "1px solid rgba(148, 163, 184, 0.3)" }}>🔒 Answer Locked</span>
                </>
              )}
            </div>
          </div>

          <div className="boson-sub-right-actions">
            {/* VIEW ORIGINAL SOURCE BUTTON */}
            {resolvedOriginalSource && (
              <button
                type="button"
                className={`btn-boson-source-toggle ${showOriginalSource ? "is-active" : ""}`}
                onClick={handleToggleOriginalSource}
                title={showOriginalSource ? "Hide original exam dump source" : "View original question from exam dump PDF"}
              >
                <svg
                  className="source-svg-icon"
                  viewBox="0 0 24 24"
                  width="15"
                  height="15"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <polyline points="14 2 14 8 20 8" />
                  <line x1="16" y1="13" x2="8" y2="13" />
                  <line x1="16" y1="17" x2="8" y2="17" />
                  <polyline points="10 9 9 9 8 9" />
                </svg>
                <span>{showOriginalSource ? "Hide Source" : "Original Source"}</span>
              </button>
            )}

            {/* ADD / EDIT QUESTION NOTE BUTTON */}
            <button
              type="button"
              className={`btn-boson-note-toggle ${
                existingComment ? "has-active-note" : ""
              }`}
              onClick={() => setIsNoteBoxOpen((prev) => !prev)}
              title={
                existingComment
                  ? "Click to view or edit your note for this question"
                  : "Click to write a note/feedback for this question"
              }
            >
              {existingComment ? (
                <>
                  <svg
                    className="note-svg-icon"
                    viewBox="0 0 24 24"
                    width="16"
                    height="16"
                    fill="currentColor"
                  >
                    <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm-2 10H6v-2h12v2zm0-3H6V7h12v2z" />
                  </svg>
                  <span>Note Attached ✓</span>
                </>
              ) : (
                <>
                  <svg
                    className="note-svg-icon"
                    viewBox="0 0 24 24"
                    width="16"
                    height="16"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                  </svg>
                  <span>Add Note</span>
                </>
              )}
            </button>

            {/* MARK FOR REVIEW BUTTON */}
            <button
              type="button"
              className={`btn-boson-flag ${isFlagged ? "is-flagged" : ""}`}
              onClick={() => onToggleFlag(seqNumber - 1)}
              title={isFlagged ? "Click to unmark review" : "Click to mark for review"}
            >
              {isFlagged ? (
                <>
                  <svg
                    className="flag-svg-icon flag-filled"
                    viewBox="0 0 24 24"
                    width="16"
                    height="16"
                    fill="currentColor"
                  >
                    <path d="M14.4 6L14 4H5v17h2v-7h5.6l.4 2h7V6h-5.6z" />
                  </svg>
                  <span>Marked for Review</span>
                </>
              ) : (
                <>
                  <svg
                    className="flag-svg-icon flag-outline"
                    viewBox="0 0 24 24"
                    width="16"
                    height="16"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1zM4 22v-7" />
                  </svg>
                  <span>Mark for Review</span>
                </>
              )}
            </button>

            {/* ADMIN EDIT QUESTION BUTTON */}
            {isUserAdmin && (
              <button
                type="button"
                className="btn-boson-edit-toggle"
                onClick={() => setIsEditModalOpen(true)}
                title="Edit this question details, options, answers, exhibits, or source dump (Admin Only)"
              >
                <svg
                  viewBox="0 0 24 24"
                  width="15"
                  height="15"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                </svg>
                <span>Edit Question</span>
              </button>
            )}

            {/* FONT RESIZE CONTROLS - ALWAYS ACCESSIBLE AT TOP IN REVIEW & EXPLANATION MODE */}
            {(isReviewMode || isRevealed || (isCommitted && settings?.showAnswersInline !== false)) && (
              <div className="explanation-font-controls header-font-controls">
                <span className="font-ctrl-label">🔤 Font:</span>
                <button
                  type="button"
                  className="font-ctrl-btn"
                  onClick={handleDecreaseFont}
                  disabled={explFontScale <= 0.8}
                  title="Decrease Font Size (-)"
                  aria-label="Decrease Font Size"
                >
                  −
                </button>
                <span className="font-ctrl-level">
                  {Math.round(explFontScale * 100)}%
                </span>
                <button
                  type="button"
                  className="font-ctrl-btn"
                  onClick={handleIncreaseFont}
                  disabled={explFontScale >= 1.8}
                  title="Increase Font Size (+)"
                  aria-label="Increase Font Size"
                >
                  +
                </button>
                <button
                  type="button"
                  className={`font-ctrl-btn reset ${explFontScale !== 1.0 ? "active" : ""}`}
                  onClick={handleResetFont}
                  title="Reset Font Size (100%)"
                  aria-label="Reset Font Size"
                >
                  ↺ Reset
                </button>
              </div>
            )}
          </div>
        </div>
        </div>
      </div>

      {/* FLOATING SIDE ARROWS (Green < and > on edges) */}
      {!isPaused && canGoPrev && (
        <button
          type="button"
          className="boson-side-arrow side-arrow-left"
          onClick={handlePrevClick}
          title="Previous Question"
          disabled={isBusyNavigating}
        >
          ‹
        </button>
      )}

      {!isPaused && canGoNext && (
        <button
          type="button"
          className={`boson-side-arrow side-arrow-right ${serverConnectionError || isBusyNavigating ? "disabled" : ""}`}
          onClick={handleNextClick}
          title="Next Question"
          disabled={isBusyNavigating || Boolean(serverConnectionError)}
        >
          ›
        </button>
      )}

      {/* QUESTION BODY AREA */}
      <div className="boson-question-body" key={seqNumber}>

        {/* INLINE QUESTION NOTE COMPOSER */}
        {isNoteBoxOpen && (
          <div className="inline-note-composer-card">
            <div className="note-composer-header">
              <div className="note-composer-title">
                <span className="note-badge">💬 Question Note / Issue Report</span>
                <span className="note-composer-sub">
                  ({question.questionNo || `Question #${seqNumber}`})
                </span>
              </div>
              <button
                type="button"
                className="note-composer-close"
                onClick={() => setIsNoteBoxOpen(false)}
                title="Close note box"
              >
                ✕
              </button>
            </div>

            <textarea
              className="note-composer-textarea"
              rows="3"
              placeholder="Describe what needs fixing (e.g. 'Option B typo', 'missing exhibit diagram', 'wrong correct answer marked', 'explanation incomplete')..."
              value={currentNoteText}
              onChange={(e) => setCurrentNoteText(e.target.value)}
              autoFocus
            />

            <div className="note-composer-footer">
              {existingComment ? (
                <button
                  type="button"
                  className="btn-note-delete"
                  onClick={() => handleDeleteComment(question?.id || questionKey, question?.questionNo, questionKey)}
                >
                  🗑️ Delete Note
                </button>
              ) : (
                <div></div>
              )}

              <div className="note-composer-right-btns">
                <button
                  type="button"
                  className="btn-note-cancel"
                  onClick={() => {
                    setCurrentNoteText(existingComment);
                    setIsNoteBoxOpen(false);
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn-note-save"
                  onClick={() => handleSaveComment(currentNoteText)}
                >
                  Save Note ✓
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ACTIVE NOTE BANNER (when note exists and composer is closed) */}
        {!isNoteBoxOpen && existingComment && (
          <div
            className="active-note-banner"
            onClick={() => setIsNoteBoxOpen(true)}
            title="Click to edit this note"
          >
            <span className="note-banner-icon">💬</span>
            <div className="note-banner-content">
              <strong>Your Note on this question:</strong> {existingComment}
            </div>
            <span className="note-banner-edit-hint">✎ Edit</span>
          </div>
        )}

        {/* ORIGINAL SOURCE DUMP VIEWER WITH INLINE ZOOM & PAN */}
        {showOriginalSource && resolvedOriginalSource && (
          <div className="boson-original-source-card">
            <div className="original-source-header">
              <div className="original-source-header-left">
                <span className="original-source-tag">📄 Original Dump Source</span>
                <span className="original-source-qno">{question.questionNo || `Question #${seqNumber}`}</span>
                {sourceZoom > 1 && (
                  <span className="source-drag-hint">↔ Drag image to explore</span>
                )}
              </div>

              <div className="original-source-controls">
                <button
                  type="button"
                  className="source-ctrl-btn"
                  onClick={handleSourceZoomOut}
                  disabled={sourceZoom <= 1}
                  title="Zoom Out (-)"
                  aria-label="Zoom Out"
                >
                  −
                </button>
                <span className="source-zoom-level">
                  {Math.round(sourceZoom * 100)}%
                </span>
                <button
                  type="button"
                  className="source-ctrl-btn"
                  onClick={handleSourceZoomIn}
                  disabled={sourceZoom >= 3.5}
                  title="Zoom In (+)"
                  aria-label="Zoom In"
                >
                  +
                </button>
                <button
                  type="button"
                  className={`source-ctrl-btn reset ${sourceZoom > 1 ? "active" : ""}`}
                  onClick={handleSourceResetZoom}
                  title="Reset Zoom & Pan"
                  aria-label="Reset Zoom"
                >
                  ⟲ Reset
                </button>
                <button
                  type="button"
                  className="source-ctrl-btn close-btn"
                  onClick={handleCloseOriginalSource}
                  title="Close Original Source"
                  aria-label="Close"
                >
                  ✕
                </button>
              </div>
            </div>

            <div
              className={`original-source-img-frame ${sourceZoom > 1 ? "is-zoomed" : ""} ${isPanningSource ? "is-panning" : ""}`}
              onMouseDown={handleSourceMouseDown}
              onMouseMove={handleSourceMouseMove}
              onMouseUp={handleSourceMouseUpOrLeave}
              onMouseLeave={handleSourceMouseUpOrLeave}
              onTouchStart={handleSourceTouchStart}
              onTouchMove={handleSourceTouchMove}
              onTouchEnd={handleSourceTouchEnd}
              onTouchCancel={handleSourceTouchEnd}
              style={{
                cursor: sourceZoom > 1 ? (isPanningSource ? "grabbing" : "grab") : "default",
                touchAction: "none",
                overflow: "hidden",
                position: "relative",
              }}
            >
              {isSourceImageLoading && !sourceImgError && (
                <div className="image-loading-overlay" role="status" aria-live="polite">
                  <span className="image-loader-spinner" aria-hidden="true"></span>
                  <span>Loading source image</span>
                </div>
              )}
              {!sourceImgError ? (
                <img
                  src={originalSourceSrc}
                  alt={`Original Dump Question for ${question.questionNo || "question"}`}
                  className="boson-original-source-img"
                  draggable={false}
                  onDragStart={(e) => e.preventDefault()}
                  style={{
                    transform: `translate(${sourcePan.x}px, ${sourcePan.y}px) scale(${sourceZoom})`,
                    transformOrigin: "center center",
                    transition: isPanningSource ? "none" : "transform 0.15s ease-out",
                    userSelect: "none",
                    pointerEvents: "auto",
                  }}
                  onLoad={() => setIsSourceImageLoading(false)}
                  onError={() => {
                    setIsSourceImageLoading(false);
                    setSourceImgError(true);
                  }}
                />
              ) : (
                <div className="source-error-wrap">
                  <p>Original source image: {resolvedOriginalSource}</p>
                </div>
              )}
            </div>
          </div>
        )}

        <div className="boson-question-prompt">
          {renderFormattedPrompt(question.question)}
        </div>

        {isMulti && settings?.showRequiredAnswersCount !== false && (
          <div className="boson-multi-indicator">
            ✋ Please select exactly <strong>{correctOptions.length} answers</strong> ({selectedIndices.length} / {correctOptions.length} selected)
          </div>
        )}

        {/* EXHIBIT IMAGE WITH INLINE ZOOM & PAN */}
        {question.exhibitImage && (
          <div className="boson-exhibit-card">
            <div className="exhibit-header">
              <div className="exhibit-header-left">
                <span className="exhibit-tag">📸 Exhibit Diagram</span>
                {exhibitZoom > 1 && (
                  <span className="exhibit-drag-hint">
                    ↔ Pinch or drag to explore
                  </span>
                )}
              </div>

              {/* + / - / RESET ZOOM CONTROLS */}
              <div className="exhibit-zoom-controls">
                <button
                  type="button"
                  className="exhibit-ctrl-btn"
                  onClick={handleZoomOut}
                  disabled={exhibitZoom <= 1}
                  title="Zoom Out (-)"
                  aria-label="Zoom Out"
                >
                  −
                </button>
                <span className="exhibit-zoom-level">
                  {Math.round(exhibitZoom * 100)}%
                </span>
                <button
                  type="button"
                  className="exhibit-ctrl-btn"
                  onClick={handleZoomIn}
                  disabled={exhibitZoom >= 3.5}
                  title="Zoom In (+)"
                  aria-label="Zoom In"
                >
                  +
                </button>
                <button
                  type="button"
                  className={`exhibit-ctrl-btn reset ${exhibitZoom > 1 ? "active" : ""}`}
                  onClick={handleResetZoom}
                  title="Reset Zoom & Pan"
                  aria-label="Reset Zoom"
                >
                  ⟲ Reset
                </button>
              </div>
            </div>

            <div
              className={`exhibit-img-frame ${exhibitZoom > 1 ? "is-zoomed" : ""} ${isPanning ? "is-panning" : ""}`}
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUpOrLeave}
              onMouseLeave={handleMouseUpOrLeave}
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
              onTouchCancel={handleTouchEnd}
              style={{
                cursor: exhibitZoom > 1 ? (isPanning ? "grabbing" : "grab") : "default",
                touchAction: "none",
                overflow: "hidden",
                position: "relative",
              }}
            >
              {isExhibitImageLoading && !imgError && (
                <div className="image-loading-overlay" role="status" aria-live="polite">
                  <span className="image-loader-spinner" aria-hidden="true"></span>
                  <span>Loading exhibit image</span>
                </div>
              )}
              {!imgError ? (
                <img
                  src={exhibitSrc}
                  alt={`Exhibit for ${question.questionNo || "question"}`}
                  className="boson-exhibit-img"
                  draggable={false}
                  onDragStart={(e) => e.preventDefault()}
                  style={{
                    transform: `translate(${exhibitPan.x}px, ${exhibitPan.y}px) scale(${exhibitZoom})`,
                    transformOrigin: "center center",
                    transition: isPanning ? "none" : "transform 0.15s ease-out",
                    userSelect: "none",
                    pointerEvents: "auto",
                  }}
                  onLoad={() => setIsExhibitImageLoading(false)}
                  onError={(e) => {
                    const clean = (question.exhibitImage || "").replace(/^\/+/, "");
                    if (e.target.src.includes(clean)) {
                      if (!e.target.dataset.triedFallback1) {
                        e.target.dataset.triedFallback1 = "true";
                        e.target.src = `/${clean}`;
                      } else {
                        setIsExhibitImageLoading(false);
                        setImgError(true);
                      }
                    } else {
                      setIsExhibitImageLoading(false);
                      setImgError(true);
                    }
                  }}
                />
              ) : (
                <div className="exhibit-error-wrap">
                  <p>Exhibit diagram: {question.exhibitImage}</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* CLI SNIPPET / CONSOLE OUTPUT */}
        {question.cliSnippet && !hasEmbeddedCliOptions(question) && (
          <div className="boson-cli-box">
            <pre className="boson-cli-content">
              <code>{question.cliSnippet}</code>
            </pre>
          </div>
        )}

        {/* RENDER DRAG & DROP OR MULTIPLE CHOICE */}
        {isDragDrop ? (
          <DragDropQuestion
            question={question}
            dispatch={dispatch}
            answer={answer}
            isReviewMode={isReviewMode || isTimeOver || isLocked}
            isLocked={isLocked}
            isInteractionDisabled={Boolean(serverConnectionError)}
            isRevealed={isRevealed}
          />
        ) : (
          <div className="boson-options-list">
            {question.options &&
              question.options.map((option, index) => {
                const match = option.match(/^([A-E])\.\s*([\s\S]*)/);
                const letter = match ? match[1] : "";
                const text = match ? match[2] : option;
                const displayText = formatOptionText(text);

                const isSelected = selectedIndices.includes(index);
                const isCorrectChoice = correctOptions.includes(index);

                let cardClass = "boson-option-item";
                if (isSelected) cardClass += " selected";
                if (isLocked) {
                  cardClass += " locked";
                  if (settings?.showAnswersInline !== false || isReviewMode || isRevealed) {
                    if (isCorrectChoice) {
                      cardClass += " correct-answer";
                    } else if (isSelected) {
                      cardClass += " wrong-answer";
                    }
                  }
                }

                return (
                  <div
                    key={index}
                    className={cardClass}
                    onClick={() => handleOptionClick(index)}
                  >
                    <div className="boson-input-indicator">
                      {isMulti ? (
                        <div className={`boson-checkbox ${isSelected ? "checked" : ""}`}>
                          {isSelected && <span className="check-tick">✓</span>}
                        </div>
                      ) : (
                        <div className={`boson-radio ${isSelected ? "checked" : ""}`}>
                          {isSelected && <span className="radio-dot"></span>}
                        </div>
                      )}
                    </div>

                    {letter && <span className="boson-option-letter">{letter}.</span>}

                    <div className="boson-option-content">
                      {displayText.includes("\n") ? (
                        <pre className="option-multiline-text">{displayText}</pre>
                      ) : (
                        <span className="option-text-inline">{displayText}</span>
                      )}
                    </div>
                  </div>
                );
              })}
          </div>
        )}

        {/* SHOW ANSWER INLINE BANNER */}
        {(isReviewMode || isRevealed || (isCommitted && settings?.showAnswersInline !== false)) && (
          <div
            className="boson-explanation-card"
            style={{ "--expl-font-scale": explFontScale }}
          >
            <div className="explanation-header-row">
              <div className="explanation-title">Answer Review</div>
              <div className="explanation-font-controls">
                <span className="font-ctrl-label">🔤 Font:</span>
                <button
                  type="button"
                  className="font-ctrl-btn"
                  onClick={handleDecreaseFont}
                  disabled={explFontScale <= 0.8}
                  title="Decrease Font Size (-)"
                  aria-label="Decrease Font Size"
                >
                  −
                </button>
                <span className="font-ctrl-level">
                  {Math.round(explFontScale * 100)}%
                </span>
                <button
                  type="button"
                  className="font-ctrl-btn"
                  onClick={handleIncreaseFont}
                  disabled={explFontScale >= 1.8}
                  title="Increase Font Size (+)"
                  aria-label="Increase Font Size"
                >
                  +
                </button>
                <button
                  type="button"
                  className={`font-ctrl-btn reset ${explFontScale !== 1.0 ? "active" : ""}`}
                  onClick={handleResetFont}
                  title="Reset Font Size (100%)"
                  aria-label="Reset Font Size"
                >
                  ↺ Reset
                </button>
              </div>
            </div>
            <div className="explanation-body">
              <div className="explanation-header-banner">
                {isDragDrop ? (
                  (() => {
                    const dndCorrect = (() => {
                      if (answer?.isCorrect !== undefined) return Boolean(answer.isCorrect);
                      const dndEval = evaluateDragDrop(question?.dragDropData, answer?.matches || {});
                      return dndEval.isAllCorrect;
                    })();
                    if (dndCorrect) {
                      return <AnswerStatus>Your Matches are Correct</AnswerStatus>;
                    }
                    return (
                      <AnswerStatus variant={isRevealed && (!answer?.matches || Object.keys(answer.matches).length === 0) ? "info" : "incorrect"}>
                        {isRevealed && (!answer?.matches || Object.keys(answer.matches).length === 0)
                          ? "Official Drag & Drop Solution & Explanations"
                          : "Your Matches are Incorrect"}
                      </AnswerStatus>
                    );
                  })()
                ) : userIsFullyCorrect ? (
                  <AnswerStatus>Your Answer is Correct</AnswerStatus>
                ) : (
                  <AnswerStatus variant="incorrect">Your Answer is Incorrect</AnswerStatus>
                )}
              </div>

              {isDragDrop ? (
                <div className="explanation-correct-labels dnd-correct-summary">
                  <div className="dnd-correct-heading">Official Correct Matches</div>
                  <div className="dnd-summary-grid">
                    {Object.entries(question.dragDropData?.correctMatches || {}).map(([target, item], idx) => (
                      <div
                        key={idx}
                        className="dnd-match-row"
                      >
                        <span className="dnd-match-field">
                          <span className="dnd-match-caption">Category</span>
                          <span className="dnd-match-target">{target}</span>
                        </span>
                        <span className="dnd-match-field dnd-match-answer">
                          <span className="dnd-match-caption">Correct match</span>
                          <span className="dnd-match-value">{item}</span>
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="explanation-correct-labels">
                  <strong>Correct Option{correctOptions.length > 1 ? "s" : ""}:</strong>
                  {correctOptions.map((optIdx) => {
                    const optText = question.options?.[optIdx] || `Option ${optIdx + 1}`;
                    const cleanText = optText.replace(/^[A-E]\.\s*/, "");
                      const displayText = formatOptionText(cleanText);
                    const letter = String.fromCharCode(65 + optIdx);
                    return (
                      <div
                        key={optIdx}
                        className="correct-option-pill"
                        style={{
                          display: "flex",
                           alignItems: "flex-start",
                          gap: "8px",
                          margin: "6px 0",
                          padding: "6px 12px",
                          background: "rgba(16, 185, 129, 0.12)",
                          border: "1px solid rgba(16, 185, 129, 0.3)",
                          borderRadius: "6px",
                          lineHeight: 1.5,
                        }}
                      >
                        <span
                          className="pill-letter"
                          style={{
                            fontWeight: 700,
                            color: "#10b981",
                            fontSize: "1.05rem",
                            letterSpacing: "0.5px",
                          }}
                        >
                          {letter}.
                        </span>
                        <span className="pill-text option-text-inline" style={{ color: "#e2e8f0" }}>
                          {displayText}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}

              {(resolveExplanation(question) || question.explanation) && (
                <div className="question-explanation-text" style={{ marginTop: "1.1rem", paddingTop: "0.95rem", borderTop: "1px solid rgba(255,255,255,0.18)" }}>
                  <div style={{ fontWeight: 700, color: "#38bdf8", marginBottom: "0.5rem", fontSize: "1.3rem", display: "flex", alignItems: "center", gap: "6px" }}>
                    <span>📖</span> Explanation & Key Concept:
                  </div>
                  {(() => {
                    const rawExpl = resolveExplanation(question) || question.explanation;
                    if (!rawExpl) return null;

                    const masterQ =
                      (Array.isArray(allQuestions) &&
                        allQuestions.find(
                          (mq) =>
                            (mq.id !== undefined && question.id !== undefined && String(mq.id) === String(question.id)) ||
                            (mq.questionNo && question.questionNo && String(mq.questionNo).trim().toLowerCase() === String(question.questionNo).trim().toLowerCase())
                        )) ||
                      getMasterQuestion(question);

                    const expl = remapExplanationToDisplayedOptions(rawExpl, masterQ?.options, question?.options);
                    const isHtml = typeof expl === "string" && /<[a-z][\s\S]*>/i.test(expl);
                    if (isHtml) {
                      return (
                        <div
                          className="explanation-rich-content"
                          dangerouslySetInnerHTML={{ __html: formatExplanation(expl) }}
                        />
                      );
                    }
                    return (
                      <div className="plain-explanation-text" style={{ color: "#f8fafc", fontSize: "1.35rem", lineHeight: "1.65", whiteSpace: "pre-line" }}>
                        {expl}
                      </div>
                    );
                  })()}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* BOTTOM ACTION TOOLBAR (Screenshot bottom bar) */}
      <div className="boson-bottom-toolbar">
        <div className="toolbar-left">
          <button
            type="button"
            className={`btn-boson-nav ${!canGoPrev ? "disabled" : ""}`}
            onClick={handlePrevClick}
            disabled={!canGoPrev || isBusyNavigating}
          >
            Previous
          </button>

          <button
            type="button"
            className={`btn-boson-nav ${!canGoNext || Boolean(serverConnectionError) ? "disabled" : ""} ${isBusyNavigating ? "is-nav-loading" : ""}`}
            onClick={handleNextClick}
            disabled={!canGoNext || isBusyNavigating || Boolean(serverConnectionError)}
          >
            {isBusyNavigating && <span className="btn-nav-spinner" aria-hidden="true"></span>}
            <span>{isBusyNavigating ? "Loading…" : "Next"}</span>
          </button>
        </div>

        <div className="toolbar-right">
          {!isReviewMode && examMode !== "simulation" && settings?.includeShowAnswerBtn !== false && (
            <button
              type="button"
              className={`btn-boson-action btn-show-answer ${isRevealed || (isCommitted && settings?.showAnswersInline !== false) || Boolean(serverConnectionError) ? "disabled" : ""}`}
              onClick={handleShowAnswerClick}
              disabled={isRevealed || (isCommitted && settings?.showAnswersInline !== false) || Boolean(serverConnectionError) || isCheckingAnswer}
            >
              {isCheckingAnswer ? "Verifying..." : (isRevealed || (isCommitted && settings?.showAnswersInline !== false) ? "✓ Answer Revealed" : "Show Answer")}
            </button>
          )}

          <button
            type="button"
            className="btn-boson-action"
            onClick={() => setShowPaletteModal(true)}
          >
            Question Review
          </button>

          {isReviewMode ? (
            <button
              type="button"
              className="btn-boson-action btn-grade btn-exit-review"
              onClick={onExitReview}
            >
              ⌂ Exit Review / Score Report
            </button>
          ) : (
            <button
              type="button"
              className="btn-boson-action btn-grade"
              onClick={handleGradeClick}
            >
              Grade Exam
            </button>
          )}
        </div>
      </div>

      {/* CUSTOM CONFIRMATION MODAL */}
      <CustomConfirmModal
        isOpen={confirmDialog.isOpen}
        title={confirmDialog.title}
        message={confirmDialog.message}
        confirmText={confirmDialog.confirmText}
        cancelText={confirmDialog.cancelText}
        type={confirmDialog.type}
        onConfirm={confirmDialog.onConfirm}
        onCancel={() => setConfirmDialog((prev) => ({ ...prev, isOpen: false }))}
      />

      {/* QUESTION REVIEW / PALETTE MODAL */}
      {showPaletteModal && (
        <QuestionPaletteModal
          numQuestions={numQuestions}
          currentIndex={seqNumber - 1}
          answers={answers}
          questions={questions}
          flaggedQuestions={flaggedQuestions}
          comments={currentBankNotes}
          revealedQuestions={revealedQuestions}
          committedQuestions={committedQuestions}
          isReviewMode={isReviewMode}
          onSelectQuestion={onGoToQuestion}
          onClose={() => setShowPaletteModal(false)}
        />
      )}

      {/* QUESTION NOTES & EXPORT MODAL */}
      {isAllNotesModalOpen && (
        <QuestionNotesModal
          comments={currentBankNotes}
          allQuestions={questions}
          onSelectQuestion={(targetIdx) => onGoToQuestion(targetIdx)}
          onDeleteComment={(targetId, targetQNo, rawKey) => handleDeleteComment(targetId, targetQNo, rawKey)}
          onClose={() => setIsAllNotesModalOpen(false)}
        />
      )}

      {/* ADMIN EDIT QUESTION MODAL */}
      {isEditModalOpen && (
        <EditQuestionModal
          isOpen={isEditModalOpen}
          question={question}
          onSaveSuccess={(updatedQ) => {
            if (dispatch) {
              dispatch({ type: "updateQuestion", payload: updatedQ });
            }
            showToast({
              type: "success",
              title: "Update Applied",
              message: `Question ${updatedQ.questionNo || "#" + updatedQ.id} updated successfully! Changes applied across all exams.`,
              duration: 5000,
            });
          }}
          onClose={() => setIsEditModalOpen(false)}
        />
      )}

      {/* FLOATING TOAST NOTIFICATION */}
      {toast && (
        <div
          className={`boson-live-edit-toast toast-${toast.type || "success"}`}
          role="status"
          aria-live="polite"
        >
          <div className="toast-icon">
            {toast.type === "error" ? "⚠️" : toast.type === "warning" ? "⚠️" : "✅"}
          </div>
          <div className="toast-content">
            <div className="toast-title">{toast.title}</div>
            <div className="toast-desc">{toast.message}</div>
          </div>
          <button
            type="button"
            className="toast-close"
            onClick={() => setToast(null)}
            title="Dismiss notification"
          >
            ✕
          </button>
        </div>
      )}

      {/* EXAM PAUSED BLUR OVERLAY & RESUME MODAL */}
      {isPaused && (
        <div
          className="exam-paused-backdrop"
          role="dialog"
          aria-modal="true"
          aria-labelledby="paused-dialog-title"
        >
          <div className="exam-paused-modal">
            <div className="paused-icon-pulse-wrapper">
              <div className="paused-pulse-glow"></div>
              <div className="paused-icon-badge">
                <svg width="30" height="30" viewBox="0 0 24 24" fill="currentColor">
                  <rect x="6" y="4" width="4.5" height="16" rx="1.5" />
                  <rect x="13.5" y="4" width="4.5" height="16" rx="1.5" />
                </svg>
              </div>
            </div>

            <h2 id="paused-dialog-title" className="paused-modal-title">
              Exam Paused
            </h2>

            <p className="paused-modal-subtitle">
              Your exam timer is stopped and question content is hidden.
              Take a break and resume whenever you are ready!
            </p>

            <div className="paused-modal-stats-grid">
              <div className="paused-stat-card">
                <span className="paused-stat-label">Current Question</span>
                <span className="paused-stat-value">#{seqNumber} of {numQuestions}</span>
              </div>
              {!isUntimed && secondsRemaining !== null && (
                <div className="paused-stat-card highlight">
                  <span className="paused-stat-label">Timer Paused At</span>
                  <span className="paused-stat-value paused-timer-highlight">
                    ⏱️ {formatTime(secondsRemaining)}
                  </span>
                </div>
              )}
              <div className="paused-stat-card">
                <span className="paused-stat-label">Exam Mode</span>
                <span className="paused-stat-value">
                  {examMode === "sim" ? "⏱️ Simulation" : "📖 Study Mode"}
                </span>
              </div>
            </div>

            <div className="paused-modal-actions">
              <button
                type="button"
                className="btn-paused-resume-primary"
                onClick={onTogglePause}
                autoFocus
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                  <polygon points="5 3 19 12 5 21 5 3" />
                </svg>
                <span>Resume Exam</span>
              </button>

              <button
                type="button"
                className="btn-paused-exit-secondary"
                onClick={handleExitClick}
              >
                <span>Save & Return to Menu</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MOBILE BOTTOM NAVIGATION BAR FOR ACTIVE EXAMS */}
      <MobileBottomBar
        isExamActive={true}
        onOpenNotes={() => setIsNoteBoxOpen(true)}
        notesCount={currentBankNotesCount}
        onOpenReviewMatrix={() => setShowPaletteModal(true)}
        onGradeExam={handleGradeClick}
        isReviewMode={isReviewMode}
        onExitReview={onExitReview}
        onToggleFlag={() => onToggleFlag(seqNumber - 1)}
        isCurrentFlagged={isFlagged}
        isPaused={isPaused}
        onTogglePause={onTogglePause}
      />
    </div>
  );
}

export default QuestionView;
